import { ReactNode, createContext, useEffect, useMemo } from 'react';

import { useGetMaterialsQuery, useGetServerMaterialsQuery, useGetOptionsQuery, useGetElementsQuery, useGetCommonElementsQuery, useGetPartsQuery, useGetFamilyPartsQuery } from './api-slice';
import { useAppDispatch, useAppSelector } from './hooks';
import { sessionIdSelector, useDataRequestLang } from './main-slice';
import { combineElementsQueries, combineMaterialQueries } from './api-helpers';
import { CatalogRoot, EMPTY_CATALOG_ROOT } from './types/catalog-internal';
import { catalogReadySelector, chosenConfiguratorSelector } from './configurator-selectors';
import { configuratorActions } from './configurator-slice';

export const CatalogContext = createContext<CatalogRoot>(EMPTY_CATALOG_ROOT);

export function CatalogLogic({ children, loading, error } : {
    loading: () => ReactNode
    error: () => ReactNode,
    children?: ReactNode
}) {

    const dispatch = useAppDispatch();

    const lang = useDataRequestLang();
    const { familyId, variantId } = useAppSelector(chosenConfiguratorSelector);
    const sessionId = useAppSelector(sessionIdSelector);
    const catalogReady = useAppSelector(catalogReadySelector);

    const materialsQ = useGetMaterialsQuery(lang);
    const serverMaterialsQ = useGetServerMaterialsQuery({lang, sessionId: sessionId!}, {skip: !sessionId});
    const optionsQ = useGetOptionsQuery({ lang, familyId, variantId }, {skip: !familyId });
    const elementsQ = useGetElementsQuery({ lang, familyId, variantId }, {skip: !familyId });
    const commonElementsQ = useGetCommonElementsQuery({ lang }, {skip: !familyId });
    const partsQ = useGetPartsQuery({ lang, familyId, variantId }, {skip: !familyId });
    const familyPartsQ = useGetFamilyPartsQuery({ lang, familyId, variantId }, {skip: !familyId });

    const [ isCatalogSuccess, isCatalogError, isCatalogLoading, catalogRoot ] = useMemo(() => {

        const isCatalogSuccess = materialsQ.isSuccess 
            && serverMaterialsQ.isSuccess 
            && optionsQ.isSuccess 
            && elementsQ.isSuccess
            && commonElementsQ.isSuccess
            && partsQ.isSuccess
            && familyPartsQ.isSuccess;

        const isCatalogError = materialsQ.isError 
            || optionsQ.isError 
            || elementsQ.isError 
            || partsQ.isError 
            || familyPartsQ.isError;

        const isCatalogLoading = ( materialsQ.isLoading 
                    || optionsQ.isLoading 
                    || elementsQ.isLoading 
                    || commonElementsQ.isLoading 
                    || partsQ.isLoading 
                    || familyPartsQ.isLoading
                )
            && !isCatalogError;

        let catalogRoot;

        if(!isCatalogSuccess) {
            catalogRoot = EMPTY_CATALOG_ROOT;
        } else {

            const parts =  partsQ.data.concat(familyPartsQ.data);
            const { tags, materials, colors } = combineMaterialQueries(serverMaterialsQ.data, materialsQ.data);
            const { elements, elementParts } = combineElementsQueries(elementsQ.data, commonElementsQ.data);


            const elementsRoot = {
                compose: elementsQ.data.compose ?? false,
                initial: elementsQ.data.initial,
                thumbnails: elementsQ.data.thumbnails,
                attachProfiles: elementsQ.data.attachProfiles,
                elementGroups: elementsQ.data.elementGroups,
                elementFilter: elementsQ.data.elementFilter,
                elements,
                elementParts,
            }

            catalogRoot = {
                materialTags: tags,
                qualities: materials,
                colors, 
                parts,
                ...elementsRoot,
                ...optionsQ.data,
                // TODO remove at some point
                elementsRoot
            }
        }

        return [ isCatalogSuccess, isCatalogError, isCatalogLoading, catalogRoot ]

    }, [ materialsQ, serverMaterialsQ, optionsQ, elementsQ, commonElementsQ, partsQ, familyPartsQ ]);

    useEffect(() => {
        if(isCatalogSuccess) {
            dispatch(configuratorActions.setCatalog(catalogRoot));
        }
    }, [ isCatalogSuccess, catalogRoot ]);

    if(isCatalogLoading) {
        return loading();
    } else if(isCatalogError) {
        return error();
    } else if(isCatalogSuccess && catalogReady) {
        return children;
    } else {
        return null;
    }
}

